import React, { useState, useEffect } from "react";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import { StreamProvider } from "@prestosports/prestostats-core/Common";
import Video32 from "@carbon/icons-react/lib/video/32";
import { Fade, Paper, Popper } from "@material-ui/core";
import { Resizable } from "react-resizable";
import { useDrag, useDrop } from "react-dnd";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import StreamStyles from "../styles/StreamStyles";
import StreamHeader from "./StreamHeader";
import StreamVideo from "./StreamVideo";
import "react-resizable/css/styles.css";
import StreamMarker from "./StreamMarker";
import { useEventContext } from "../../../state/events/context";
import { useStreamContext } from "../../../state/stream/context";
import { GameRoutes } from "../../../routes/game-routes";
import EmptyStream from "./EmptyStream";
import { useFootballContext } from "../../../state/football-play/context";
import { useGameContextContext } from "../../../state/game-context/context";
import { isDefined } from "../../../util/object-util";

const Stream = ({ popout }) => {
	const { eventId } = useParams();

	const { state: eventState, selectEventStream } = useEventContext();
	const currentEvent = eventState.currentEvent();
	const { selectedStreamId } = currentEvent || {};

	const {
		state: streamState,
		saveStreamMarker,
		deleteStreamMarker,
		getProviderSrc,
		toggleStreamOpen,
		resetTimeSkip,
		setPopoutOpen,
		setPopoutClosed
	} = useStreamContext();
	const streams = streamState.streams();
	const streamOpen = streamState.open();
	const timeSkip = streamState.timeSkip();
	const popoutOpen = streamState.popoutOpen();
	const stream = streams?.find((s) => s.id === selectedStreamId) || streams?.[0];
	const markers = stream?.markers || [];

	const { state: footballState } = useFootballContext();
	const footballPlays = footballState.filteredPlays();

	const { state: contextState } = useGameContextContext();
	const gameConfig = contextState.gameConfig();

	const [videoPlayer, setVideoPlayer] = useState(null);
	const [snapshotMap, setSnapshotMap] = useState({});
	const snapshot = snapshotMap[stream?.id];

	const [providerLookupArr, setProviderLookupArr] = useState([]);

	const [defaultSize] = useState({ width: 360, height: 360 });
	const [screenSize, setScreenSize] = useState({ width: 0, height: 0 });
	const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
	const [size, setSize] = useState({ width: 0, height: 0 });

	const onOpenPopout = () => {
		toggleStreamOpen(false);
		setPopoutOpen();
		window.open(
			`${GameRoutes.STREAM.url.replace(":eventId", currentEvent.id)}`,
			"_blank",
			"popup=true"
		);
	};

	const captureStreamSnapshot = (player) => {
		const { selectedId } = player || {};

		if (selectedId) {
			const streamSnapshot = {
				volume: player.volume(),
				currentTime: player.currentTime(),
				paused: player.paused(),
				duration:
					player.duration() === Infinity
						? Math.max(player.currentTime(), snapshot?.duration || 0)
						: player.duration()
			};

			setSnapshotMap((prevState) => ({
				...prevState,
				[selectedId]: streamSnapshot
			}));
		}
	};

	const onMarkerSave = (marker) => {
		saveStreamMarker(stream.id, marker);
	};

	const onMarkerDelete = (markerId) => {
		deleteStreamMarker(stream.id, markerId);
	};

	const handleStreamSelection = (id) => {
		selectEventStream(currentEvent.id, id);
	};

	const getControlProps = ({ width, height }) => {
		const { width: screenWidth, height: screenHeight } =
			document.body.getBoundingClientRect() || {};
		const defaultPosition = {
			x: screenWidth - width,
			y: screenHeight - height
		};
		return { defaultPosition, screenHeight, screenWidth };
	};

	const isOOB = ({ width, height }, { x, y }) => {
		return width + x > screenSize.width || height + y > screenSize.height;
	};

	const handleDrag = ({ x, y }) => {
		setDragPos({
			x: Math.max(0, Math.min(x, screenSize.width - size.width)),
			y: Math.max(0, Math.min(y, screenSize.height - size.height))
		});
	};

	const handleResize = (e, { size: newSize }) => {
		if (!isOOB(newSize, dragPos)) {
			setSize(newSize);
		}
	};

	const [{ isDragging }, drag, preview] = useDrag(() => ({
		type: "div",
		collect: (monitor) => ({
			isDragging: !!monitor.isDragging()
		})
	}));

	// eslint-disable-next-line no-unused-vars
	const [dropProps, drop] = useDrop(
		{
			accept: "div",
			drop: (_, monitor) => {
				handleDrag(monitor.getSourceClientOffset());
			}
		},
		[handleDrag]
	);

	drop(document.body);

	useEffect(() => {
		if (streamOpen) {
			const { defaultPosition, screenHeight, screenWidth } =
				getControlProps(defaultSize);
			setScreenSize({ width: screenWidth, height: screenHeight });
			setDragPos(defaultPosition);
			setSize({ width: defaultSize.width, height: defaultSize.height });
		}
	}, [streamOpen, defaultSize]);

	useEffect(() => {
		const getProviderSrcStale = () =>
			stream.isProviderSrcStale ||
			(stream.providerCode !== StreamProvider.None &&
				!providerLookupArr.some((key) => key === stream.id));

		if (!stream) {
			toggleStreamOpen(false);
		} else if (getProviderSrcStale()) {
			setProviderLookupArr((prevState) => [...prevState, stream.id]);
			getProviderSrc(stream.id);
		}
	}, [stream]);

	useEffect(() => {
		const isTimeSkipAllowed =
			isDefined(timeSkip) &&
			videoPlayer &&
			!videoPlayer.isDisposed() &&
			videoPlayer.hasMetadataLoaded;

		if (isTimeSkipAllowed) {
			videoPlayer.currentTime(timeSkip);
			videoPlayer.play();
			resetTimeSkip();
		}
	}, [timeSkip, videoPlayer]);

	useEffect(() => {
		if (popout) {
			setPopoutOpen();
			if (currentEvent.id !== eventId) {
				window.close();
			}
		}
	}, [streams, popout]);

	useEffect(() => {
		if (popout) {
			window.addEventListener("unload", setPopoutClosed);

			return () => {
				window.removeEventListener("unload", setPopoutClosed);
			};
		}
		return () => {};
	}, []);

	const classes = StreamStyles();

	const getStreamContent = () => {
		return (
			<>
				<StreamHeader
					handleClose={() => toggleStreamOpen(false)}
					handleStreamSelection={handleStreamSelection}
					streams={streams}
					stream={stream}
					dragRef={drag}
					onOpenPopout={onOpenPopout}
					popout={popout}
				/>
				<StreamVideo
					setVideoPlayer={setVideoPlayer}
					snapshot={snapshot}
					streamSrc={stream.src}
					streamId={stream.id}
					captureStreamSnapshot={captureStreamSnapshot}
					timeSkip={timeSkip}
					resetTimeSkip={resetTimeSkip}
				/>
				<StreamMarker
					markers={markers}
					videoPlayer={videoPlayer}
					snapshot={snapshot}
					onDelete={onMarkerDelete}
					onSave={onMarkerSave}
					footballPlays={footballPlays}
					gameConfig={gameConfig}
				/>
			</>
		);
	};

	return (
		<>
			{popout && stream && (
				<Box className={classes.streamPopoutBox}>{getStreamContent()}</Box>
			)}
			{popout && !stream && <EmptyStream />}
			{!popout && stream && (
				<Box>
					<Popper
						ref={preview}
						className={classNames(classes.popper, {
							[classes.popperHidden]: isDragging
						})}
						style={{ top: dragPos.y, left: dragPos.x }}
						open={streamOpen}
						placement="bottom"
						transition
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						{({ TransitionProps }) => (
							<Fade {...TransitionProps} timeout={350}>
								<Resizable
									onResize={handleResize}
									className={classes.resizable}
									width={size.width}
									height={size.height}
									minConstraints={[defaultSize.width, defaultSize.height]}
									lockAspectRatio
									resizeHandles={["se"]}
								>
									<Paper
										style={{ width: size.width, height: size.height }}
										onClick={(e) => e.stopPropagation()}
										className={classes.paper}
									>
										{getStreamContent()}
									</Paper>
								</Resizable>
							</Fade>
						)}
					</Popper>
					{!!streams?.length && !popout && (
						<IconButton
							onClick={(e) => {
								e.stopPropagation();
								if (!popoutOpen) {
									toggleStreamOpen(!streamOpen);
								}
							}}
							aria-label="Toggle stream"
							color={streamOpen || popoutOpen ? "primary" : "secondary"}
						>
							<Video32 />
						</IconButton>
					)}
				</Box>
			)}
		</>
	);
};

export default Stream;
